import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  CreditCard,
  KeyRound,
  LayoutDashboard,
  LogIn,
  LogOut,
  Menu,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  X,
} from 'lucide-react';
import './Header.css';
import SettingsPanel from './SettingsPanel';
import { useSettings } from '../settings';

const LogoIcon = () => (
  <>
    <img
      src="/awn_logo.webp"
      alt="AWN AdPulse logo"
      className="brand-logo-img"
      onError={(e) => { e.currentTarget.classList.add('is-missing'); }}
      style={{ width: '34px', height: '34px', borderRadius: '8px', objectFit: 'cover' }}
    />
    <span className="brand-logo-fallback" aria-hidden="true">A</span>
  </>
);

const LogoLockup = ({ onClick, platformLabel }) => (
  <button type="button" className="brand-lockup brand-lockup-btn" onClick={onClick} title="Back to home">
    <div className="logo-mark" style={{ background: 'none', boxShadow: 'none' }}>
      <LogoIcon />
    </div>
    <div style={{ textAlign: 'left' }}>
      <p className="eyebrow">{platformLabel}</p>
      <h1>AWN AdPulse</h1>
    </div>
  </button>
);

const navBtnStyle = (active) => ({
  background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', padding: 0,
  color: active ? '#fff' : 'var(--muted)',
});

function AccountControls({ user, onSignIn, onSignOut, onOpenAccount, onOpenSettings }) {
  const { t } = useSettings();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.key === 'Escape' || (event.type === 'mousedown' && !rootRef.current?.contains(event.target))) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  const select = (section) => {
    setOpen(false);
    onOpenAccount(section);
  };

  if (user) {
    return (
      <div className="account-menu-root" ref={rootRef}>
        <button
          type="button"
          className={`account-chip ${open ? 'is-open' : ''}`}
          onClick={() => setOpen((current) => !current)}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <span className="account-avatar">{(user.name || user.email || 'U').charAt(0).toUpperCase()}</span>
          <span className="account-name">{user.name || user.email}</span>
          <ChevronDown size={15} className="account-chevron" />
        </button>
        {open && (
          <div className="account-dropdown" role="menu">
            <div className="account-dropdown-identity">
              <strong>{user.name || 'AWN AdPulse user'}</strong>
              <small>{user.email}</small>
            </div>
            <button type="button" role="menuitem" onClick={() => select('profile')}><UserRound size={16} /> Account / Profile Settings</button>
            <button type="button" role="menuitem" onClick={() => select('subscription')}><CreditCard size={16} /> Subscription Plans</button>
            <button type="button" role="menuitem" onClick={() => select('subscription')}><CreditCard size={16} /> Subscription Cancellation</button>
            <button type="button" role="menuitem" onClick={() => select('security')}><KeyRound size={16} /> Change / Reset Password</button>
            <button type="button" role="menuitem" onClick={() => select('privacy')}><ShieldCheck size={16} /> Privacy &amp; Data</button>
            <button type="button" role="menuitem" onClick={() => { setOpen(false); onOpenSettings(); }}><Settings size={16} /> App Preferences</button>
            <button type="button" role="menuitem" className="account-dropdown-logout" onClick={() => { setOpen(false); onSignOut(); }}><LogOut size={16} /> Logout</button>
          </div>
        )}
      </div>
    );
  }
  return (
    <button className="secondary-button" onClick={onSignIn} style={{ gap: '8px', fontSize: '13px' }}>
      <LogIn size={15} /> {t('nav.signIn')}
    </button>
  );
}

function Header({ currentView, onChangeView, user, onSignIn, onSignOut, onOpenAccount }) {
  const [scrolled, setScrolled] = useState(false);
  const scrolledRef = useRef(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { t } = useSettings();
  const isAppView = currentView === 'workspace' || currentView === 'editor' || currentView === 'brandhub' || currentView === 'dashboard';

  useEffect(() => {
    setMobileNavOpen(false);
  }, [currentView]);

  useEffect(() => {
    const openSettings = () => setSettingsOpen(true);
    window.addEventListener('adpulse:open-settings', openSettings);
    return () => window.removeEventListener('adpulse:open-settings', openSettings);
  }, []);

  const changeView = (nextView) => {
    setMobileNavOpen(false);
    onChangeView(nextView);
  };

  // Landing header turns into a frosted-white bar once the user scrolls past the hero top.
  useEffect(() => {
    if (isAppView) {
      if (scrolledRef.current) {
        scrolledRef.current = false;
        setScrolled(false);
      }
      return undefined;
    }

    let frameId = 0;
    const updateScrolled = () => {
      frameId = 0;
      const nextScrolled = window.scrollY > 40;
      if (scrolledRef.current !== nextScrolled) {
        scrolledRef.current = nextScrolled;
        setScrolled(nextScrolled);
      }
    };
    const onScroll = () => {
      if (!frameId) frameId = window.requestAnimationFrame(updateScrolled);
    };
    updateScrolled();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', onScroll);
    };
  }, [isAppView]);

  if (isAppView) {
    return (
      <>
        <header className="topbar">
          <LogoLockup onClick={() => changeView('landing')} platformLabel={t('nav.platformLabel')} />

          <button
            type="button"
            className="product-mobile-nav-toggle"
            aria-label={mobileNavOpen ? 'Close workspace navigation' : 'Open workspace navigation'}
            aria-controls="product-navigation"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <nav id="product-navigation" className={`nav-links product-navigation ${mobileNavOpen ? 'is-open' : ''}`}>
            <button className={`nav-link ${currentView === 'workspace' ? 'active' : ''}`} onClick={() => changeView('workspace')} style={navBtnStyle(currentView === 'workspace')}>
              {t('nav.campaigns')}
            </button>
            <button className={`nav-link ${currentView === 'brandhub' ? 'active' : ''}`} onClick={() => changeView('brandhub')} style={navBtnStyle(currentView === 'brandhub')}>
              {t('nav.brandHub')}
            </button>
            <button className={`nav-link ${currentView === 'editor' ? 'active' : ''}`} onClick={() => changeView('editor')} style={navBtnStyle(currentView === 'editor')}>
              {t('nav.graphicsSuite')}
            </button>
            <button className={`nav-link ${currentView === 'dashboard' ? 'active' : ''}`} onClick={() => changeView('dashboard')} style={{ ...navBtnStyle(currentView === 'dashboard'), display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <LayoutDashboard size={15} /> {t('nav.analytics')}
            </button>
            <button className="nav-link settings-nav-button" type="button" onClick={() => setSettingsOpen(true)}>
              <SlidersHorizontal size={15} /> {t('nav.settings')}
            </button>
            <AccountControls
              user={user}
              onSignIn={onSignIn}
              onSignOut={onSignOut}
              onOpenAccount={onOpenAccount}
              onOpenSettings={() => setSettingsOpen(true)}
            />
            <button className="secondary-button" onClick={() => changeView('landing')} style={{ gap: '8px', fontSize: '13px' }}>
              <ArrowLeft size={16} /> {t('nav.site')}
            </button>
          </nav>
        </header>
        <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </>
    );
  }

  return (
    <>
      <header className={`topbar topbar--landing ${scrolled ? 'is-scrolled' : ''}`}>
        <LogoLockup onClick={() => onChangeView('landing')} platformLabel={t('nav.platformLabel')} />

        <nav className="nav-links">
          <a href="#home" className="nav-link">{t('nav.home')}</a>
          <a href="#contact" className="nav-link">{t('nav.contact')}</a>
          <button className="nav-link settings-nav-button" type="button" onClick={() => setSettingsOpen(true)}>
            <SlidersHorizontal size={15} /> {t('nav.settings')}
          </button>
          <AccountControls
            user={user}
            onSignIn={onSignIn}
            onSignOut={onSignOut}
            onOpenAccount={onOpenAccount}
            onOpenSettings={() => setSettingsOpen(true)}
          />
          <button onClick={() => onChangeView('workspace')} className="lp-nav-cta" type="button">
            {t('nav.launchApp')}
          </button>
        </nav>
      </header>
      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}

export default Header;
